import { Trans, useLingui } from '@lingui/react/macro';
import { LayoutTemplate, Plus } from 'lucide-react';
import { authStoreUserTournamentId } from 'modules/auth/store/selectors';
import { MediaManagerTemplateGroup } from 'modules/manager/domain/interfaces/media-manager-template-group';
import { useMediaManagerTemplateGroupsQuery } from 'modules/manager/store/media-manager-api';
import MediaManagerTemplateGroupCard from 'modules/manager/ui/components/media-manager-template-group-card';
import { PRESET_GRID_CLASS } from 'modules/manager/ui/components/preset-card-metrics';
import { TemplateGroupGridSkeleton } from 'modules/manager/ui/components/skeletons';
import useCreateTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-create-template-group-modal';
import useDeleteTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-delete-template-group-modal';
import useRenameTemplateGroupModal from 'modules/manager/ui/hooks/modals/use-rename-template-group-modal';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { deriveAsyncState } from 'shared/utils/async-state';
import classNames from 'classnames';
import UDButton from 'ud-ui/components/button';
import UDHeaderTitle from 'ud-ui/components/header-title';
import { UDText } from 'ud-ui/components';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';

const MediaManagerTemplatesListPage: React.FC = () => {
  const { t } = useLingui();
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const tournamentId = useSelector(authStoreUserTournamentId);

  const {
    data: groups = [],
    currentData,
    isFetching,
    isError,
    refetch,
  } = useMediaManagerTemplateGroupsQuery({ tournamentId: tournamentId ?? 0 }, { skip: !tournamentId });
  const listState = deriveAsyncState({
    itemsCount: groups.length,
    hasCurrentData: currentData !== undefined,
    isFetching,
    isError,
  });

  useEffect(() => {
    setHeaderContent(
      <UDHeaderTitle>
        <Trans>Медиа менеджер</Trans>
      </UDHeaderTitle>,
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadGroups = useCallback(() => {
    void refetch();
  }, [refetch]);

  const existingNames = useMemo(() => groups.map((group) => group.name.trim()), [groups]);
  const { modal: createModal, open: openCreateModal } = useCreateTemplateGroupModal({
    tournamentId: tournamentId ?? null,
    existingNames,
    onCreated: loadGroups,
  });
  const { modal: renameModal, open: openRenameModal } = useRenameTemplateGroupModal({
    groups,
    onUpdated: loadGroups,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteTemplateGroupModal({
    onDeleted: loadGroups,
  });

  const openGroup = useCallback(
    (group: MediaManagerTemplateGroup) => {
      navigate(Pages.MEDIA_MANAGER.builders.group(group.id));
    },
    [navigate],
  );

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-end gap-3">
        <UDButton variant="secondary" className="flex items-center" onClick={() => navigate(Pages.MEDIA_MANAGER.TEMPLATES_LIBRARY)}>
          <LayoutTemplate size={16} className="mr-2" />
          <Trans>Общие шаблоны</Trans>
        </UDButton>
        <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          <Trans>Создать группу</Trans>
        </UDButton>
      </div>

      {listState.status === 'loading' ? (
        <TemplateGroupGridSkeleton canWrite />
      ) : listState.status === 'error' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2" role="alert">
          <UDText variant="body-2">
            <Trans>Не удалось загрузить группы шаблонов</Trans>
          </UDText>
          <UDButton variant="secondary" onClick={loadGroups}>
            <Trans>Повторить</Trans>
          </UDButton>
        </div>
      ) : listState.status === 'empty' ? (
        <div className="flex h-full flex-col items-center justify-center gap-2">
          <UDText variant="body-2" className="text-gray-500">
            <Trans>Пока нет групп шаблонов</Trans>
          </UDText>
          <UDButton variant="primary" onClick={openCreateModal}>
            <Plus size={16} className="mr-2" /> <Trans>Создать группу</Trans>
          </UDButton>
        </div>
      ) : (
        <div
          className={classNames(PRESET_GRID_CLASS, 'transition-opacity', {
            'pointer-events-none opacity-50': listState.isRefreshing,
          })}
          aria-busy={listState.isRefreshing}
        >
          {groups.map((group) => (
            <MediaManagerTemplateGroupCard
              key={group.id}
              group={group}
              canWrite
              onOpen={openGroup}
              onRename={openRenameModal}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      {createModal}
      {renameModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerTemplatesListPage;
